import { useAuth } from "@clerk/clerk-react";
import { Box, ButtonBase, IconButton, Typography } from "@mui/material";
import { getMarkRange } from "@tiptap/core";
import { Editor as TipTapEditor } from "@tiptap/react";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext } from "react";

import { OrganizationTemplateRubricSuggestion } from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";

import styles from "./styles";

interface OverusedPhrasesSuggestionPopupProps {
  editor: TipTapEditor;
  suggestion: OrganizationTemplateRubricSuggestion;
  onApplied?: (suggestionId: number, beforeHtml: string) => void;
  onDismiss: () => void | Promise<void>;
}

const OverusedPhrasesSuggestionPopup = ({
  editor,
  suggestion,
  onApplied,
  onDismiss,
}: OverusedPhrasesSuggestionPopupProps) => {
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);

  const markApplied = async (beforeHtml: string): Promise<void> => {
    if (!currentOrg?.id) return;
    await fetcherAuth(
      getToken,
      `/api/organization/${currentOrg.id}/organization-template-rubric-suggestions/suggestion/${suggestion.id}`,
      "PATCH",
      {},
      { status: "applied" },
    );
    onApplied?.(suggestion.id, beforeHtml);
  };

  const handlePickExample = async (example: string): Promise<void> => {
    const highlightMark = editor.state.schema.marks.highlight;
    if (!highlightMark) return;
    const { from } = editor.state.selection;
    const $pos = editor.state.doc.resolve(from);
    const range = getMarkRange($pos, highlightMark);
    if (!range) return;

    const beforeHtml = editor.getHTML();

    editor
      .chain()
      .focus()
      .setTextSelection({ from: range.from, to: range.to })
      .insertContent(example)
      .run();

    await markApplied(beforeHtml);
  };

  const handleGotIt = (): void => {
    const highlightMark = editor.state.schema.marks.highlight;
    if (!highlightMark) return;
    const { from } = editor.state.selection;
    const $pos = editor.state.doc.resolve(from);
    const range = getMarkRange($pos, highlightMark);
    if (!range) return;

    editor
      .chain()
      .focus()
      .setTextSelection(range.to + 1)
      .run();
  };

  const examples =
    suggestion.examples ??
    (suggestion.proposedReplacement ? [suggestion.proposedReplacement] : []);

  return (
    <Box sx={styles.container}>
      <Box sx={styles.head}>
        <Box sx={styles.avatar}>
          <Box
            component="i"
            className="fa-solid fa-sparkles"
            sx={styles.avatarIcon}
          />
        </Box>
        <Box sx={styles.helperBlock}>
          <Typography sx={styles.helperText}>Bento helper</Typography>
          <Box sx={styles.kindRow}>
            <Box component="span" sx={styles.kindDot} />
            Heads-up · Overused phrases
          </Box>
        </Box>
        <IconButton size="small" sx={styles.closeButton} onClick={onDismiss}>
          <Box
            component="i"
            className="fa-regular fa-xmark"
            sx={styles.closeIcon}
          />
        </IconButton>
      </Box>

      <Box sx={styles.body}>
        {suggestion.title && (
          <Typography sx={styles.title}>{suggestion.title}</Typography>
        )}
        <Typography sx={styles.rationale}>{suggestion.body}</Typography>

        {examples.length > 0 && (
          <Box sx={styles.examplesCard}>
            <Typography sx={styles.examplesHead}>
              Try something specific like…
            </Typography>
            <Box sx={styles.examplesList}>
              {examples.map((example) => (
                <ButtonBase
                  key={example}
                  sx={styles.exampleItem}
                  onClick={() => handlePickExample(example)}
                >
                  <Box component="span" sx={styles.exampleArrow}>
                    →
                  </Box>
                  <Box component="span">
                    {example}
                    <Box component="span" sx={styles.exampleTryHint}>
                      Try this
                    </Box>
                  </Box>
                </ButtonBase>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      <Box sx={styles.actions}>
        <ButtonBase sx={styles.primaryButton} onClick={handleGotIt}>
          <Box
            component="i"
            className="fa-solid fa-check"
            sx={styles.primaryIcon}
          />
          Got it
        </ButtonBase>
        <ButtonBase sx={styles.rejectButton} onClick={onDismiss}>
          <Box
            component="i"
            className="fa-regular fa-xmark"
            sx={styles.rejectIcon}
          />
        </ButtonBase>
      </Box>
    </Box>
  );
};

export default OverusedPhrasesSuggestionPopup;
